import React, { useState, useEffect } from 'react';
import { useApp, defaultLinks, defaultSettings } from '../context/AppContext';
import {
  FiEdit2, FiSave, FiRefreshCcw, FiYoutube, FiLoader, FiCheckCircle, 
  FiAlertCircle, FiDatabase, FiExternalLink, FiUploadCloud, 
  FiRotateCcw, FiToggleLeft, FiToggleRight, FiGlobe, FiType
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AdminLogin from '../components/AdminLogin';
import { apiService } from '../services/api';
import { auth } from '../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';

const Admin = () => {
  const { links, settings, updateLink, updateSettings, isLoading, isLive, refresh, setIsLive } = useApp();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('cards');
  const [editingId, setEditingId] = useState(null);
  const [cardFormData, setCardFormData] = useState({});
  const [settingsFormData, setSettingsFormData] = useState(settings);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [syncStep, setSyncStep] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (sessionStorage.getItem('gc_admin_auth') === 'true') setIsAuthenticated(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (settings) setSettingsFormData(settings);
  }, [settings]);

  const handleLogout = () => {
    sessionStorage.clear();
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) return <AdminLogin onSuccess={() => setIsAuthenticated(true)} />;

  const showStatus = (msg, isErr = false) => {
    setStatusMessage({ text: msg, isError: isErr });
    setTimeout(() => setStatusMessage(null), 10000);
  };

  const handleSaveCard = async () => {
    setIsSaving(true);
    try {
      await updateLink(editingId, cardFormData);
      setEditingId(null);
      showStatus('Card updated successfully!');
      await refresh();
    } catch (err) {
      showStatus(err.message, true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await updateSettings(settingsFormData);
      showStatus('Site content and SEO updated!');
      await refresh();
    } catch (err) {
      showStatus(err.message, true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRepair = async () => {
    setIsSaving(true);
    setSyncStep('Restoring defaults...');
    try {
      await apiService.seedDatabase(defaultLinks, defaultSettings);
      await refresh();
      showStatus('✅ Site restored to defaults!');
    } catch (err) {
      showStatus(err.message, true);
    } finally {
      setIsSaving(false);
      setSyncStep('');
    }
  };

  if (isLoading) return (
    <div className="flex flex-col items-center justify-center min-h-[50vh]">
      <SafeIcon icon={FiLoader} className="text-4xl text-brand-green animate-spin mb-4" />
      <p className="text-gray-500 font-serif">Connecting to Firestore...</p>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      {statusMessage && (
        <div className={`fixed top-24 right-4 z-50 flex items-start gap-3 px-6 py-4 rounded-xl shadow-2xl border ${statusMessage.isError ? 'bg-red-600 text-white' : 'bg-brand-dark text-brand-gold'}`}>
          <SafeIcon icon={statusMessage.isError ? FiAlertCircle : FiCheckCircle} className="text-xl mt-0.5" />
          <span className="text-sm font-bold">{statusMessage.text}</span>
        </div>
      )}

      <div className="mb-8 flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-2xl font-serif font-bold text-brand-dark">Management Dashboard</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`}></span>
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              {isLive ? 'Live Sync Active' : 'Local Mode'}
            </span>
          </div>
        </div>
        <div className="flex gap-3 mt-4 md:mt-0">
          <button onClick={refresh} className="p-2 text-gray-400 hover:text-brand-green transition-colors"><SafeIcon icon={FiRefreshCcw} /></button>
          <button onClick={handleLogout} className="text-red-500 text-sm font-bold px-4 py-2 bg-red-50 rounded-lg">Sign Out</button>
        </div>
      </div>

      <div className="flex gap-8 mb-8 border-b overflow-x-auto whitespace-nowrap">
        <button onClick={() => setActiveTab('cards')} className={`pb-4 font-bold text-xs uppercase tracking-widest ${activeTab === 'cards' ? 'text-brand-green border-b-2 border-brand-green' : 'text-gray-400'}`}>Hub Cards</button>
        <button onClick={() => setActiveTab('copy')} className={`pb-4 font-bold text-xs uppercase tracking-widest ${activeTab === 'copy' ? 'text-brand-green border-b-2 border-brand-green' : 'text-gray-400'}`}>Homepage Copy</button>
        <button onClick={() => setActiveTab('seo')} className={`pb-4 font-bold text-xs uppercase tracking-widest ${activeTab === 'seo' ? 'text-brand-green border-b-2 border-brand-green' : 'text-gray-400'}`}>SEO & GEO</button>
      </div>

      {activeTab === 'cards' && (
        <div className="grid gap-4">
          {links.map(link => (
            <div key={link.id} className="bg-white p-4 rounded-2xl border border-gray-100 flex items-center gap-4">
              <img src={link.imageUrl} className="w-16 h-12 object-cover rounded-lg" alt="" />
              <div className="flex-grow">
                <h4 className="font-bold text-brand-dark text-sm">{link.title}</h4>
                <p className="text-[10px] text-gray-400 font-mono truncate max-w-xs">{link.url}</p>
              </div>
              <button onClick={() => { setEditingId(link.id); setCardFormData({...link}); }} className="p-2 text-brand-green hover:bg-brand-light rounded-lg">
                <SafeIcon icon={FiEdit2} />
              </button>
            </div>
          ))}
          
          {editingId && (
            <div className="fixed inset-0 bg-brand-dark/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
              <div className="bg-white w-full max-w-xl rounded-3xl p-8 shadow-2xl">
                <h3 className="text-xl font-serif font-bold mb-6">Edit Card Link</h3>
                <div className="space-y-4">
                  <input className="w-full border p-3 rounded-xl outline-none" value={cardFormData.title} onChange={e => setCardFormData({...cardFormData, title: e.target.value})} placeholder="Title" />
                  <input className="w-full border p-3 rounded-xl outline-none font-mono text-sm" value={cardFormData.url} onChange={e => setCardFormData({...cardFormData, url: e.target.value})} placeholder="Hyperlink URL" />
                  <textarea className="w-full border p-3 rounded-xl h-24 text-sm" value={cardFormData.description} onChange={e => setCardFormData({...cardFormData, description: e.target.value})} placeholder="Description" />
                  <div className="flex items-center justify-between p-3 bg-brand-light rounded-xl">
                    <span className="text-sm font-bold">Coming Soon?</span>
                    <button onClick={() => setCardFormData({...cardFormData, isComingSoon: !cardFormData.isComingSoon})} className="text-2xl text-brand-green">
                      <SafeIcon icon={cardFormData.isComingSoon ? FiToggleRight : FiToggleLeft} />
                    </button>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button onClick={() => setEditingId(null)} className="flex-grow py-3 text-gray-400 font-bold">Cancel</button>
                    <button onClick={handleSaveCard} className="flex-grow py-3 bg-brand-green text-white rounded-xl font-bold">Save Card</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'copy' && (
        <div className="space-y-6">
          <div className="bg-white p-8 rounded-3xl border border-gray-100 space-y-6 shadow-sm">
            <h3 className="text-lg font-serif font-bold text-brand-green flex items-center gap-2">
              <SafeIcon icon={FiType} /> Hero Section
            </h3>
            <div className="grid gap-4">
              <input className="w-full border p-3 rounded-xl" value={settingsFormData.home.heroTitle} onChange={e => setSettingsFormData({...settingsFormData, home: {...settingsFormData.home, heroTitle: e.target.value}})} placeholder="Hero Title" />
              <input className="w-full border p-3 rounded-xl" value={settingsFormData.home.heroSubtitle} onChange={e => setSettingsFormData({...settingsFormData, home: {...settingsFormData.home, heroSubtitle: e.target.value}})} placeholder="Hero Subtitle" />
              <textarea className="w-full border p-3 rounded-xl h-20 text-sm" value={settingsFormData.home.heroDescription} onChange={e => setSettingsFormData({...settingsFormData, home: {...settingsFormData.home, heroDescription: e.target.value}})} placeholder="Hero Description" />
            </div>

            <h3 className="text-lg font-serif font-bold text-brand-green flex items-center gap-2 pt-4">
               Intro Statement (Italic Paragraph)
            </h3>
            <textarea className="w-full border p-4 rounded-xl h-32 text-sm italic" value={settingsFormData.home.introQuote} onChange={e => setSettingsFormData({...settingsFormData, home: {...settingsFormData.home, introQuote: e.target.value}})} />

            <h3 className="text-lg font-serif font-bold text-brand-green flex items-center gap-2 pt-4">
               Lower Section Headers
            </h3>
            <div className="grid gap-4">
              <input className="w-full border p-3 rounded-xl" value={settingsFormData.home.findHereTitle} onChange={e => setSettingsFormData({...settingsFormData, home: {...settingsFormData.home, findHereTitle: e.target.value}})} placeholder="What You'll Find Title" />
              <input className="w-full border p-3 rounded-xl" value={settingsFormData.home.whoIsForTitle} onChange={e => setSettingsFormData({...settingsFormData, home: {...settingsFormData.home, whoIsForTitle: e.target.value}})} placeholder="Who This Is For Title" />
            </div>

            <button onClick={handleSaveSettings} className="w-full bg-brand-green text-white py-4 rounded-2xl font-bold shadow-lg hover:bg-brand-dark transition-all">
              Update Site Copy
            </button>
          </div>
        </div>
      )}

      {activeTab === 'seo' && (
        <div className="space-y-6">
          <div className="bg-white p-8 rounded-3xl border border-gray-100 space-y-6 shadow-sm">
            <h3 className="text-lg font-serif font-bold text-brand-dark flex items-center gap-2">
              <SafeIcon icon={FiGlobe} /> Global SEO Settings
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">Browser Tab Title</label>
                <input className="w-full border p-3 rounded-xl" value={settingsFormData.seo.metaTitle} onChange={e => setSettingsFormData({...settingsFormData, seo: {...settingsFormData.seo, metaTitle: e.target.value}})} />
              </div>
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">Meta Description (Google Snippet)</label>
                <textarea className="w-full border p-3 rounded-xl h-24 text-sm" value={settingsFormData.seo.metaDescription} onChange={e => setSettingsFormData({...settingsFormData, seo: {...settingsFormData.seo, metaDescription: e.target.value}})} />
              </div>
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">Keywords (Comma Separated)</label>
                <input className="w-full border p-3 rounded-xl" value={settingsFormData.seo.keywords} onChange={e => setSettingsFormData({...settingsFormData, seo: {...settingsFormData.seo, keywords: e.target.value}})} />
              </div>
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">YouTube Channel URL</label>
                <div className="relative">
                  <input className="w-full border p-3 pl-10 rounded-xl font-mono text-xs" value={settingsFormData.youtubeUrl} onChange={e => setSettingsFormData({...settingsFormData, youtubeUrl: e.target.value})} />
                  <SafeIcon icon={FiYoutube} className="absolute left-3 top-1/2 -translate-y-1/2 text-red-600" />
                </div>
              </div>
            </div>

            <button onClick={handleSaveSettings} className="w-full bg-brand-green text-white py-4 rounded-2xl font-bold shadow-lg hover:bg-brand-dark transition-all">
              Save SEO & Global Settings
            </button>
          </div>

          <div className="bg-brand-dark p-8 rounded-3xl text-white border border-brand-gold/30">
            <h2 className="text-xl font-serif font-bold text-brand-gold mb-2 flex items-center gap-2">
              <SafeIcon icon={FiRotateCcw} /> Emergency Restore
            </h2>
            <p className="text-white/60 text-sm mb-6">Reset everything back to the original Golden Compass defaults.</p>
            <button onClick={handleRepair} className="bg-brand-gold text-brand-dark px-8 py-3 rounded-full font-bold uppercase tracking-widest hover:bg-white transition-all">
              {isSaving ? syncStep || 'Repairing...' : 'Restore All Defaults'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;